import React, { useState, useEffect } from 'react';
import { NativeBiometric } from '@capgo/capacitor-native-biometric';

const BiometricAuthComponent = ({ onAuthSuccess, onAuthFailure }) => {
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricType, setBiometricType] = useState(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  useEffect(() => {
    checkBiometricAvailability();
  }, []);

  const checkBiometricAvailability = async () => {
    try {
      const result = await NativeBiometric.isAvailable();
      setBiometricAvailable(result.isAvailable);
      if (result.isAvailable) {
        setBiometricType(result.biometryType);
      }
      console.log('Biometric availability:', result);
    } catch (error) {
      console.error('Biometric availability check failed:', error);
      setBiometricAvailable(false);
    }
  };

  const authenticateUser = async () => {
    if (!biometricAvailable) {
      if (onAuthFailure) {
        onAuthFailure('Autenticação biométrica não disponível');
      }
      return;
    }

    setIsAuthenticating(true);

    try {
      const result = await NativeBiometric.verifyIdentity({
        reason: 'Por favor, autentique-se para acessar sua conta',
        title: 'Autenticação Biométrica',
        subtitle: 'Use Face ID, Touch ID ou sua digital',
        description: 'Coloque seu dedo no sensor ou olhe para a câmera',
        negativeText: 'Cancelar',
        maxAttempts: 3,
      });

      console.log('Authentication result:', result);
      
      if (onAuthSuccess) {
        onAuthSuccess(result);
      }
    } catch (error) {
      console.error('Biometric authentication error:', error);
      
      let errorMessage = 'Falha na autenticação';
      
      // Handle specific error codes
      if (error.message) {
        if (error.message.includes('UserCancel')) {
          errorMessage = 'Autenticação cancelada pelo usuário';
        } else if (error.message.includes('UserFallback')) {
          errorMessage = 'Usuário escolheu método alternativo';
        } else if (error.message.includes('BiometryNotAvailable')) {
          errorMessage = 'Autenticação biométrica não disponível';
        } else if (error.message.includes('BiometryNotEnrolled')) {
          errorMessage = 'Nenhuma biometria cadastrada no dispositivo';
        } else if (error.message.includes('BiometryLockout')) {
          errorMessage = 'Autenticação biométrica temporariamente bloqueada';
        }
      }
      
      if (onAuthFailure) {
        onAuthFailure(errorMessage);
      }
    } finally {
      setIsAuthenticating(false);
    }
  };

  const getBiometricTypeDisplay = () => {
    if (!biometricType) return 'Autenticação Biométrica';
    
    switch (biometricType) {
      case 'FACE_ID':
        return 'Face ID';
      case 'TOUCH_ID':
        return 'Touch ID';
      case 'FINGERPRINT':
        return 'Digital';
      case 'FACE_AUTHENTICATION':
        return 'Reconhecimento Facial';
      default:
        return 'Autenticação Biométrica';
    }
  };

  const getBiometricIcon = () => {
    if (!biometricType) return '🔐';
    
    switch (biometricType) {
      case 'FACE_ID':
        return '🤳';
      case 'TOUCH_ID':
        return '👆';
      case 'FINGERPRINT':
        return '👆';
      case 'FACE_AUTHENTICATION':
        return '🤳';
      default:
        return '🔐';
    }
  };

  return (
    <div className="biometric-auth p-4">
      <h3 className="text-lg font-semibold mb-4">🔐 Autenticação Biométrica</h3>
      
      {biometricAvailable ? (
        <div className="auth-available">
          <div className="text-center mb-4">
            <div className="text-4xl mb-2">{getBiometricIcon()}</div>
            <h4 className="text-md font-medium">Acesso Seguro</h4>
            <p className="text-sm text-gray-600">
              Use {getBiometricTypeDisplay()} para acessar sua conta com segurança
            </p>
          </div>
          
          <button 
            onClick={authenticateUser}
            disabled={isAuthenticating}
            className="w-full p-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {isAuthenticating 
              ? 'Autenticando...' 
              : `🔓 Usar ${getBiometricTypeDisplay()}`
            }
          </button>
          
          <div className="mt-3 text-xs text-gray-500 text-center">
            Tipo detectado: {biometricType || 'Não especificado'}
          </div>
        </div>
      ) : (
        <div className="auth-unavailable text-center">
          <div className="text-4xl mb-2">❌</div>
          <h4 className="text-md font-medium mb-2">Biometria Indisponível</h4>
          <p className="text-sm text-gray-600">
            A autenticação biométrica não está disponível neste dispositivo. 
            Use o login tradicional com usuário e senha.
          </p>
          
          <button 
            onClick={checkBiometricAvailability}
            className="mt-3 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
          >
            🔄 Verificar Novamente
          </button>
        </div>
      )}
    </div>
  );
};

export default BiometricAuthComponent;